/**
 * UN8 questionnaire — faith, character, history, home, ancestry, temperament.
 * (photographed 07 Aug 2026). Additive: `UserApplication` gains one optional
 * `answers` field, so every existing screen and store keeps working untouched.
 */

/* ─────────────── shared primitives ─────────────── */

/** Faith scale is 10 % … 100 %. Stored as the integer percent. */
export type FaithPercent = 10 | 20 | 30 | 40 | 50 | 60 | 70 | 80 | 90 | 100;

/** Explicit tri-state. `null` = not answered yet; never conflate with `false`. */
export type YesNo = boolean | null;

export type Frequency = "frequent" | "seldom" | "never" | null;

/* ─────────────── faith ─────────────── */

export interface FaithAnswers {
  /** "Serving G-d — how important in your life?" 10–100. */
  servingImportance: FaithPercent | null;
  /** Self-declared observance of the commandments. */
  keepsCommandments: YesNo;
  grewUpInChurch: YesNo;
  churchAttendance: Frequency;
  spreadsGospel: YesNo;
  /** "Do you have idols?" — questionnaire wording. */
  hasIdols: YesNo;
}

/* ─────────────── character & conduct ─────────────── */

export interface CharacterAnswers {
  peacemaker: YesNo;
  homebuilder: YesNo;
  humorous: YesNo;
  caringAffectionate: YesNo;
  /** Wants a household free of fighting. */
  wantsPeacefulHome: YesNo;
  goodManners: YesNo;
  /** Self-rated honesty on answering this questionnaire, 1–10. */
  honestyRating: number | null;
}

/* ─────────────── history & readiness ─────────────── */

export interface HistoryAnswers {
  usedMarijuana: YesNo;
  usedLsd: YesNo;
  usedOtherDrugs: YesNo;
  wasAddicted: YesNo;
  stableLovingChildhood: YesNo;
  /** "Any scars / damaged?" */
  carriesScars: YesNo;
  /** Hard gate input: healed and ready, or not yet. */
  readyForLastingRelationship: YesNo;
}

/* ─────────────── appearance ─────────────── */

export interface AppearanceAnswers {
  hasTattoos: YesNo;
  /** Whether tattoos on a partner are acceptable. */
  acceptsPartnerTattoos: YesNo;
}

/* ─────────────── home & self-sufficiency ─────────────── */

export interface HomeAnswers {
  herbKnowledge: YesNo;
  plantsOwnFruitVeg: YesNo;
  interestedInFruitForest: YesNo;
  preppingForCalamities: YesNo;
  /** Amish / Mennonite style community farming and independent living. */
  communityFarming: YesNo;
  canningAndPreserving: YesNo;
  survivalSkills: YesNo;
  specialSkills: string;
}

/* ─────────────── children ─────────────── */

export type ChildrenStance =
  | "wants"
  | "does_not_want"
  | "open"
  | "has_and_living_with"
  | null;

/* ─────────────── ancestry ─────────────── */

/**
 * Origins list. Kept as free-form codes so the set can grow without a
 * migration; the UI supplies the picker.
 */
export type OriginCode = string;

export interface AncestryAnswers {
  /** The member's own declared bloodlines. */
  own: OriginCode[];
  /** Origins they would accept in a partner. `["any"]` = no preference. */
  accepted: OriginCode[];
}

/* ─────────────── interests ─────────────── */

/** Interest tick-list. Stored as codes; labels live in the UI layer. */
export type InterestCode = string;

/* ─────────────── free response ─────────────── */

export interface FreeResponse {
  describeYourself: string;
  lifePurpose: string;
  /** "How do you see you and the love of your life in 15 years?" */
  fifteenYearVision: string;
  weaknesses: string;
}

/* ─────────────── conduct flags ─────────────── */

/** Reported by other members. Never contributes to a score. */
export type ConductFlag =
  | "bullying"
  | "rudeness"
  | "no_manners"
  | "jealousy"
  | "dishonest_profile";

/* ─────────────── the whole set ─────────────── */

export interface ProfileAnswers {
  faith: FaithAnswers;
  character: CharacterAnswers;
  history: HistoryAnswers;
  appearance: AppearanceAnswers;
  home: HomeAnswers;
  children: ChildrenStance;
  /** Minimum faith percent this member requires in a partner. Hard gate. */
  requiredPartnerFaith: FaithPercent | null;
  /** Whether past drug use is acceptable in a partner. Hard gate. */
  acceptsPastSubstanceUse: YesNo;
  ancestry: AncestryAnswers;
  interests: InterestCode[];
  free: FreeResponse;
}

/** Empty answer set — use as the initial onboarding state. */
export function emptyProfileAnswers(): ProfileAnswers {
  return {
    faith: {
      servingImportance: null,
      keepsCommandments: null,
      grewUpInChurch: null,
      churchAttendance: null,
      spreadsGospel: null,
      hasIdols: null,
    },
    character: {
      peacemaker: null,
      homebuilder: null,
      humorous: null,
      caringAffectionate: null,
      wantsPeacefulHome: null,
      goodManners: null,
      honestyRating: null,
    },
    history: {
      usedMarijuana: null,
      usedLsd: null,
      usedOtherDrugs: null,
      wasAddicted: null,
      stableLovingChildhood: null,
      carriesScars: null,
      readyForLastingRelationship: null,
    },
    appearance: { hasTattoos: null, acceptsPartnerTattoos: null },
    home: {
      herbKnowledge: null,
      plantsOwnFruitVeg: null,
      interestedInFruitForest: null,
      preppingForCalamities: null,
      communityFarming: null,
      canningAndPreserving: null,
      survivalSkills: null,
      specialSkills: "",
    },
    children: null,
    requiredPartnerFaith: null,
    acceptsPastSubstanceUse: null,
    ancestry: { own: [], accepted: [] },
    interests: [],
    free: {
      describeYourself: "",
      lifePurpose: "",
      fifteenYearVision: "",
      weaknesses: "",
    },
  };
}
